// Feature-usage analytics via Umami custom events.
//
// The Umami tracker (components/layout/umami-script.tsx) only loads in
// production deployments where NEXT_PUBLIC_UMAMI_WEBSITE_ID is set, so
// trackEvent must be a safe no-op everywhere else (local dev, staging,
// Playwright e2e, ad-blocked browsers).

export type UmamiEventData = Record<string, string | number>;

declare global {
  interface Window {
    umami?: { track: (name: string, data?: UmamiEventData) => void };
  }
}

// script.js is deferred, so events fired on first paint can beat it. Queue
// them and retry briefly; if the tracker never appears (staging build,
// ad-blocker) give up for the rest of the page lifetime so the queue can't
// grow unbounded.
const MAX_FLUSH_ATTEMPTS = 10;
const FLUSH_INTERVAL_MS = 1000;

let pending: Array<{ name: string; data?: UmamiEventData }> = [];
let flushAttempts = 0;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let gaveUp = false;

function flushPending(): void {
  flushTimer = null;
  if (window.umami) {
    const queued = pending;
    pending = [];
    for (const event of queued) window.umami.track(event.name, event.data);
    return;
  }
  flushAttempts += 1;
  if (flushAttempts >= MAX_FLUSH_ATTEMPTS) {
    gaveUp = true;
    pending = [];
    return;
  }
  flushTimer = setTimeout(flushPending, FLUSH_INTERVAL_MS);
}

export function trackEvent(name: string, data?: UmamiEventData): void {
  if (typeof window === "undefined") return;
  if (process.env.NODE_ENV === "development") {
    console.debug("[umami]", name, data);
    return;
  }
  if (window.umami) {
    window.umami.track(name, data);
    return;
  }
  if (gaveUp) return;
  pending.push({ name, data });
  if (!flushTimer) flushTimer = setTimeout(flushPending, FLUSH_INTERVAL_MS);
}

// Collapses dynamic path segments to "_" so URLs aggregate by route pattern:
// /elections/3f2a…-uuid/lists/9c1b…-uuid/call -> /elections/_/lists/_/call.
//
// Fail-closed on purpose: every static route segment in this app is lowercase
// ASCII plus hyphens, so anything outside that (or implausibly long) is
// treated as dynamic. Length heuristics alone are not enough here: the
// short-link capability tokens in public URLs (styrkja/sjalfbodalidi, see
// backend app/security/tokens.py) are only 12 chars and must never reach
// analytics. Cost of the strictness: a future static segment with a digit or
// underscore would show up as "_" until this rule learns about it.
export function normalizePath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const normalized = segments.map((segment) => {
    if (/[^a-z-]/.test(segment)) return "_";
    if (segment.length >= 24) return "_";
    return segment;
  });
  return `/${normalized.join("/")}`;
}
